import { HTMLInputTypeAttribute, forwardRef, useCallback } from 'react'

import {
  FireStyledErrorText,
  FireStyledErrorTextContainer,
  FireStyledIconContainer,
  StyledInput,
  StyledInputContainer,
} from './FireFormInput.styles'
import { FireFormLargeError } from './FireFormLargeError'

export interface FireFormInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  hasError?: boolean
  errorTexts?: string[]
  startIcon?: React.ReactNode
  endIcon?: React.ReactNode
  isLarge?: boolean
  isLargeError?: boolean
  type?: HTMLInputTypeAttribute
  onClear?: () => void
}

export const FireFormInput = forwardRef<HTMLInputElement, FireFormInputProps>(
  (
    {
      errorTexts,
      isLargeError = false,
      hasError,
      startIcon,
      endIcon,
      isLarge,
      className,
      type = 'text',
      onClear,
      ...inputProps
    },
    ref
  ) => {
    const handleOnChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        inputProps.onChange?.(e)

        const nativeEvent = e.nativeEvent as InputEvent
        if (
          type === 'search' &&
          e.target.value === '' &&
          nativeEvent &&
          nativeEvent.inputType === undefined
        ) {
          onClear?.()
        }
      },
      [inputProps, onClear, type]
    )

    return (
      <>
        <StyledInputContainer className={className}>
          {startIcon && (
            <FireStyledIconContainer $iconPosition="start">
              {startIcon}
            </FireStyledIconContainer>
          )}
          <StyledInput
            {...inputProps}
            ref={ref}
            $hasError={hasError}
            $hasStartIcon={!!startIcon}
            $hasEndIcon={!!endIcon}
            $isLarge={isLarge}
            as="input"
            $level={isLarge ? 'L' : 'S'}
            type={type}
            onChange={handleOnChange}
          />
          {endIcon && (
            <FireStyledIconContainer $iconPosition="end">
              {endIcon}
            </FireStyledIconContainer>
          )}
        </StyledInputContainer>
        <FireStyledErrorTextContainer $hasError={hasError}>
          {errorTexts?.map((errorText, index) =>
            isLargeError ? (
              <FireFormLargeError key={index} errorText={errorText} />
            ) : (
              <FireStyledErrorText key={index}>{errorText}</FireStyledErrorText>
            )
          )}
        </FireStyledErrorTextContainer>
      </>
    )
  }
)

export default FireFormInput
